/**
 * JWT Authentication Client
 * Handles token storage, retrieval, and refresh logic for NestJS backend
 */

import type { SignInMethod } from '../last-sign-in-method';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3002/api/v1';

/**
 * Auth is unversioned — `/api/auth`, not `/api/v1/auth`.
 *
 * The URI version is for entity REST, where a resource's shape can change under clients. Signing
 * in has no such shape, and pinning it to v1 makes the version something auth has to move in
 * lockstep with — including the refresh cookie's Path, which fails SILENTLY when it drifts.
 * Derived from the configured base so it follows host and protocol without a second setting.
 */
export const AUTH_URL = `${API_URL.replace(/\/v\d+\/?$/, '')}/auth`;

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
// Junk a browser may still be carrying: nothing mints an Invite Session (ADR-0003), so these keys
// and the routing flag beside them grant nothing. They are cleared, never read.
const INVITE_TOKEN_PREFIX = 'invite_token:';
const INVITE_SESSION_COOKIE = 'invite_session';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  organisationName?: string;
}

export interface PendingVerification {
  email: string;
  accountStatus: 'unverified';
}

export interface VerifiedSession extends AuthTokens {
  accountStatus?: string;
  requiresOrgSetup?: boolean;
}

export interface AuthUser {
  _id?: string;
  email: string;
  name?: string;
  organisationId?: string;
  organisationName?: string;
  role?: string;
  image?: string;
  isScoped?: boolean;
  isActive?: boolean;
  accountStatus?: 'unverified' | 'invited' | 'active';
  /** Set when a business-domain signup has no organisation yet and must create one. */
  requiresOrgSetup?: boolean;
  /** Principal type. Absent for a real user document; 'partner' for a partner principal. */
  type?: 'partner';
}

/*
 * Nothing writes a token to client storage any more, which is why there is no `setTokens` here.
 * Every way in — login, register, refresh, the OAuth callback, an emailed code and an
 * invitation's code — sets an HttpOnly cookie server-side that the browser attaches on its own
 * and no script can read. A second copy in localStorage would hand one XSS both, and buys
 * nothing: `refreshAccessToken` already works without one. Reads stay, for the sessions and the
 * server-set cookies that predate this.
 */

/**
 * Wipe anything the previous occupant of this browser left in client storage.
 *
 * Called on a successful sign-in. The new session is a cookie, so nothing needs to be written —
 * but signing in as someone else must not inherit what the last occupant left, and no leftover
 * invite token or marker should outlive them.
 */
export function clearClientSessionRemnants(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && key.startsWith(INVITE_TOKEN_PREFIX)) localStorage.removeItem(key);
  }
  document.cookie = `${INVITE_SESSION_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
}

/**
 * Get refresh token from localStorage or cookie
 * Tries localStorage first, then falls back to cookie
 */
export function getRefreshToken(): string | null {
  if (typeof window !== 'undefined') {
    // Try localStorage first
    const localToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (localToken) return localToken;

    // Fallback to cookie (set by server-side OAuth callback)
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === REFRESH_TOKEN_KEY) {
        // Also sync to localStorage for consistency
        localStorage.setItem(REFRESH_TOKEN_KEY, value);
        return value;
      }
    }
  }
  return null;
}

/**
 * Clear all tokens (logout)
 * Removes from both localStorage AND cookies
 */
export function clearTokens(): void {
  if (typeof window === 'undefined') return;
  clearClientSessionRemnants();
  document.cookie = 'access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
}

/**
 * Login with email and password
 */
export async function login(credentials: LoginCredentials): Promise<AuthTokens> {
  // The route is `/auth/login`. This posted `/auth/signin`, which 404s — password sign-in
  // through the form never reached the API at all.
  const response = await fetch(`${AUTH_URL}/login`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Login failed');
  }

  const data = await response.json();
  const tokens: AuthTokens = {
    accessToken: data.accessToken || data.access_token,
    refreshToken: data.refreshToken || data.refresh_token,
  };

  // Not stored: /auth/login set the session as an HttpOnly cookie. Keeping a second copy in
  // localStorage would hand it straight back to any XSS and defeat the cookie entirely.
  //
  // Whatever the last occupant of this browser left behind IS cleared, though — stale invite
  // tokens and markers included. Signing in as someone else must not inherit their records.
  clearClientSessionRemnants();
  return tokens;
}

/**
 */
export async function register(data: RegisterData): Promise<PendingVerification> {
  const response = await fetch(`${AUTH_URL}/register`, {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...data, frontendBaseUrl: window.location.origin }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Registration failed');
  }

  return response.json();
}

export async function verifyEmail(token: string): Promise<VerifiedSession> {
  const response = await fetch(`${AUTH_URL}/verify-email`, {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'This confirmation link is no longer valid.');
  }

  clearClientSessionRemnants();
  return response.json();
}

export async function resendVerification(email: string): Promise<{ message: string }> {
  const response = await fetch(`${AUTH_URL}/resend-verification`, {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, frontendBaseUrl: window.location.origin }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Could not send a new link.');
  }

  return response.json();
}

/**
 * Refresh access token using refresh token
 */
export async function refreshAccessToken(): Promise<string | null> {
  // May be null for a cookie-only session: the refresh cookie is HttpOnly and scoped to this
  // endpoint, so the browser attaches it without JS ever seeing it. Bailing out on a missing
  // localStorage copy would make every ordinary session unrefreshable.
  const refreshToken = getRefreshToken();

  try {
    const response = await fetch(`${AUTH_URL}/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {}),
      },
    });

    if (!response.ok) {
      // Refresh token is invalid or expired
      clearTokens();
      return null;
    }

    const data = await response.json();
    const newAccessToken = data.accessToken || data.access_token;
    const newRefreshToken = data.refreshToken || data.refresh_token;

    // A cookie-only session gets nothing back here — /auth/refresh rotated the HttpOnly cookie
    // and the browser follows. A session holding a localStorage copy must be told, or the next
    // refresh presents the token this call just retired.
    if (newRefreshToken && typeof window !== 'undefined') {
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
    }

    return newAccessToken;
  } catch (error) {
    console.error('Token refresh failed:', error);
    clearTokens();
    return null;
  }
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();

  // Always, and never `if (refreshToken)`: the session is an HttpOnly cookie, so there is nothing
  // in localStorage to find and nothing `document.cookie` can clear. The server call is the only
  // thing that clears it, and skipping it left someone who pressed Sign out still signed in — the
  // access token sat in the jar for seven days and middleware only checks that one is present.
  try {
    await fetch(`${AUTH_URL}/logout`, {
      credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });
  } catch (error) {
    console.error('Logout API call failed:', error);
  }

  // Clear tokens from localStorage
  clearTokens();
}

/**
 * Fetch current user profile from API
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  // The session is an HttpOnly cookie the browser attaches itself — there is no token here to
  // send, and nothing to check before asking.
  try {
    const response = await fetch(`${AUTH_URL}/me`, { credentials: 'include' });

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired, try to refresh
        const newToken = await refreshAccessToken();
        if (newToken) {
          const retryResponse = await fetch(`${AUTH_URL}/me`, { credentials: 'include' });
          if (retryResponse.ok) {
            return await retryResponse.json();
          }
        }
        // Refresh failed, clear tokens
        clearTokens();
        return null;
      }
      throw new Error('Failed to fetch user profile');
    }

    const user = await response.json();
    return user;
  } catch (error) {
    console.error('Error fetching current user:', error);
    return null;
  }
}

/**
 * OAuth login redirect helper
 */
export function loginWithGoogle(email?: string): void {
  window.location.href = `${AUTH_URL}/google?${oauthQuery(email)}`;
}

// The address decides which Entra directory the person is sent to (ADR-0037 clause 7), so it
// travels with the redirect rather than being asked for again at the provider.
export function loginWithMicrosoft(email?: string): void {
  window.location.href = `${AUTH_URL}/microsoft?${oauthQuery(email)}`;
}

function oauthQuery(email?: string): string {
  const params = new URLSearchParams({ returnUrl: window.location.origin });
  if (email) params.set('email', email);
  return params.toString();
}

export type { SignInMethod };

export type SignInBranch =
  | { branch: 'redirect'; provider: 'google' | 'microsoft' }
  | { branch: 'methods'; methods: SignInMethod[] };

/**
 * The branch is the API's to decide (ADR-0024): the web never holds its own copy of which
 * methods an organisation offers.
 */
export async function resolveSignIn(email: string): Promise<SignInBranch> {
  const response = await fetch(`${AUTH_URL}/resolve-sign-in`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Could not continue with that address');
  }

  return response.json();
}

/**
 * The address an invitation link's pointer names, or null. Never throws: a pointer that names
 * nobody, or an answer that never arrives, leaves the page asking for the address as it would
 * anyone.
 */
export async function invitedAddress(pointer: string): Promise<string | null> {
  try {
    const response = await fetch(`${AUTH_URL}/invite-address`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invite: pointer }),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { email?: string | null };
    return body.email ?? null;
  } catch {
    return null;
  }
}

/** The invitation pointer a link carries, on the page itself or inside where it will return to. */
export function invitePointerIn(search: URLSearchParams, callbackUrl: string): string | null {
  const direct = search.get('invite');
  if (direct) return direct;
  try {
    return new URL(callbackUrl, 'http://placeholder.invalid').searchParams.get('invite');
  } catch {
    return null;
  }
}

/** The invitation pointer on the page's own URL, once a page has rendered in the browser. */
export function invitePointerOnPage(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('invite');
}

export async function resolveSignInForOrganisation(
  organisation: string,
): Promise<SignInBranch> {
  const response = await fetch(
    `${AUTH_URL}/resolve-sign-in/${encodeURIComponent(organisation)}`,
    { credentials: 'include' },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Could not continue with that link');
  }

  return response.json();
}

export async function requestSignInCode(email: string): Promise<void> {
  const response = await fetch(`${AUTH_URL}/request-otp`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Could not send a code');
  }
}

export async function verifySignInCode(email: string, otp: string): Promise<AuthTokens> {
  const response = await fetch(`${AUTH_URL}/verify-otp`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'That code was not accepted');
  }

  const data = await response.json();
  const accessToken = data.accessToken || data.access_token;
  // A correct code for an address with no account answers 200 and no session — the
  // person has proved the address and has nowhere to go yet. Storing that as tokens
  // writes "undefined" into the jar and bounces them off middleware forever.
  if (!accessToken) {
    throw new Error(
      data.message || 'That address has no Partnr account yet. Register to finish setting one up.',
    );
  }

  const tokens: AuthTokens = {
    accessToken,
    refreshToken: data.refreshToken || data.refresh_token,
  };
  // The session is the HttpOnly cookie the API just set, and `refreshAccessToken` works without
  // a localStorage copy — the refresh cookie is scoped to that endpoint and travels on its own.
  // A second copy here would hand one XSS both, which is why `login` stores nothing either.
  clearClientSessionRemnants();
  return tokens;
}

/**
 * Handle OAuth callback (extract tokens from URL)
 */
export function handleOAuthCallback(): AuthTokens | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const accessToken = params.get('access_token') || params.get('accessToken');
  const refreshToken = params.get('refresh_token') || params.get('refreshToken');

  if (accessToken && refreshToken) {
    const tokens: AuthTokens = { accessToken, refreshToken };
    // app/auth/callback/route.ts already wrote these as HttpOnly cookies before redirecting.

    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname);

    return tokens;
  }

  return null;
}
